/**
 * @file The ExchangeDayDetailComponent is used as detail view for the inbox and outbox to display exchange day requests.
 * @author Maarten Lensink
 */
import * as tslib_1 from "tslib";
import { FormControl } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { DomainService } from '../../domain.service';
import { UserService } from '../../user.service';
import { AlertsService } from '../../core/alerts.service';
import { DialogService } from '../../core/dialog.service';
import { LocalisationService } from '../../core/localisation.service';
import { RelationBase } from '../../base/relation/relation.base';
import { NotificationsService } from '../notifications.service';
import { ExchangeDetailComponent } from '../exchange-detail/exchange-detail.component';
import { SettingsService } from '../../settings.service';
/**
 * The ExchangeDayDetailComponent is used as detail view for the inbox and outbox to display exchange day requests.
 * @export
 * @class ExchangeDayDetailComponent
 * @extends {ExchangeDetailComponent}
 */
var ExchangeDayDetailComponent = /** @class */ (function (_super) {
    tslib_1.__extends(ExchangeDayDetailComponent, _super);
    /**
     * Creates an instance of ExchangeDayDetailComponent.
     * @param {UserService} userService
     * @param {Router} router
     * @param {ActivatedRoute} route
     * @param {NotificationsService} notificationsService
     * @param {DialogService} dialogService
     * @param {AlertsService} alertsService
     * @param {TranslateService} translateService
     * @param {DomainService} domainService The service is used to retrieve the sender and receiver resources.
     * @memberOf ExchangeDayDetailComponent
     */
    function ExchangeDayDetailComponent(userService, router, route, notificationsService, dialogService, alertsService, translateService, domainService, settingsService, localisationService) {
        var _this = _super.call(this, userService, router, route, notificationsService, dialogService, alertsService, settingsService, translateService) || this;
        _this.domainService = domainService;
        _this.localisationService = localisationService;
        /**
         * The field property of the fieldset container for the request's receiver.
         * @type {FieldConfiguration}
         * @memberOf ExchangeDayDetailComponent
         */
        _this.resourceField = {
            name: '@this',
            labelKey: 'APP.MISC.RESOURCE.#SINGULAR',
            readonly: true,
            editor: 'entity',
            format: {
                entity: 'Resource',
                fields: [
                    'FirstName',
                    'Prefix',
                    'Name'
                ]
            }
        };
        /**
         * FormControl to bind with the select fieldset.
         * This is needed for the angular input validation mechanism.
         * The fieldset displays the status of the request.
         * @memberOf ExchangeDetailComponent
         */
        _this.resourceControl = new FormControl();
        return _this;
    }
    Object.defineProperty(ExchangeDayDetailComponent.prototype, "resource", {
        /**
         * Returns the opposing resource of the user's resource.
         * @readonly
         * @type {Entity}
         * @memberOf ExchangeDayDetailComponent
         */
        get: function () {
            return this.isOutbox ? this.receiver : this.sender;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ExchangeDayDetailComponent.prototype, "subjectResource", {
        /**
         * Returns the current owner of the subject task list.
         * @readonly
         * @type {Entity}
         * @memberOf ExchangeDayDetailComponent
         */
        get: function () {
            if (!this.request) {
                return;
            }
            return this.request.Status === 'ACCEPT' ? this.receiver : this.sender;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ExchangeDayDetailComponent.prototype, "targetResource", {
        /**
         * Returns the current owner of the target task list.
         * @readonly
         * @type {Entity}
         * @memberOf ExchangeDayDetailComponent
         */
        get: function () {
            if (!this.request) {
                return;
            }
            return this.request.Status === 'ACCEPT' ? this.sender : this.receiver;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ExchangeDayDetailComponent.prototype, "showDetails", {
        /**
         * Gets a value indicating whether the details of a day should be shown.
         * @readonly
         * @type {boolean}
         * @memberOf ExchangeDayDetailComponent
         */
        get: function () {
            return this.request && (this.request.Status === 'REQUEST' || this.request.Status === 'ACCEPT');
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Returns the label dependent on inbox or outbox and the request type.
     * @param {string} what subject | resource | target
     * @returns {string} An i18n label.
     * @memberOf ExchangeDayDetailComponent
     */
    ExchangeDayDetailComponent.prototype.getLabel = function (what) {
        if (!this.request) {
            return null;
        }
        var result = 'APP.NOTIFICATIONS.';
        result += this.isOutbox ? 'OUTBOX_' : 'INBOX_';
        result += this.request.TargetDay ? 'EXCHANGE_' : 'REASSIGN_';
        result += what.toUpperCase();
        return result;
    };
    /**
     * Returns a value indicating whether the request has been loaded.
     * @returns {boolean} True if the request has been loaded.
     * @memberOf ExchangeDetailComponent
     */
    ExchangeDayDetailComponent.prototype.canSave = function () {
        return this.request != null && !this.request.IsExpired &&
            this.subjectDayFormObject && this.subjectDayFormObject.items && this.subjectDayFormObject.items.length > 0 &&
            (this.request.TargetDay == null || (this.targetDayFormObject && this.targetDayFormObject.items && this.targetDayFormObject.items.length > 0));
    };
    /**
     * Returns a datetime on the given date at the end of the day.
     * @param {Date} date The date, which should be at start of day.
     * @returns {Date} The date at the end of the day.
     * @memberOf ExchangeDayDetailComponent
     */
    ExchangeDayDetailComponent.prototype.endOf = function (date) {
        if (!date) {
            return null;
        }
        var result = new Date(date.valueOf());
        result.setDate(date.getDate() + 1);
        return result;
    };
    /**
     * Returns a string representing the date.
     * @param {Date} date The date to be displayed.
     * @returns {string} A date in localised format.
     * @memberOf ExchangeDayDetailComponent
     */
    ExchangeDayDetailComponent.prototype.displayDate = function (date) {
        return moment(date).format(this.localisationService.currentLanguage.formats.date.format);
    };
    /**
     * Sets the request, the sender, and the receiver.
     * @protected
     * @param {Request} value
     * @memberOf ExchangeDayDetailComponent
     */
    ExchangeDayDetailComponent.prototype.setRequest = function (value) {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var resourceType, _a, _b;
            return tslib_1.__generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        _super.prototype.setRequest.call(this, value);
                        if (!this.request) {
                            return [2 /*return*/];
                        }
                        resourceType = this.domainService.getEntityType('Resource');
                        _a = this;
                        return [4 /*yield*/, this.domainService.getEntityByKey(resourceType, this.request.SenderOid).toPromise()];
                    case 1:
                        _a.sender = (_c.sent());
                        _b = this;
                        return [4 /*yield*/, this.domainService.getEntityByKey(resourceType, this.request.ReceiverOid).toPromise()];
                    case 2:
                        _b.receiver = (_c.sent());
                        return [2 /*return*/];
                }
            });
        });
    };
    return ExchangeDayDetailComponent;
}(ExchangeDetailComponent));
export { ExchangeDayDetailComponent };
